import { useCallback } from 'react';

import { useSendFeedback, useSendFeedbackMessage } from '@/store/sessions';
import { CHAT_MESSAGE_RATING } from '@/types/chat';

export interface UseMessageFeedbackParams {
  messageId: string;
  sessionId?: string | null;
  rating: CHAT_MESSAGE_RATING | null;
  onRatingChange?: (rating: CHAT_MESSAGE_RATING) => void;
}

export interface UseMessageFeedbackResult {
  isLiked: boolean;
  isDisliked: boolean;
  isRated: boolean;
  handleLikeClick: () => void;
  handleDislikeClick: () => void;
  submitFeedback: (data: {
    feedback: string;
    reasons: string[];
  }) => Promise<void>;
}

export function useMessageFeedback({
  messageId,
  sessionId,
  rating,
  onRatingChange,
}: UseMessageFeedbackParams): UseMessageFeedbackResult {
  const { mutateAsync: sendFeedback } = useSendFeedback();
  const { mutateAsync: sendFeedbackMessage } = useSendFeedbackMessage();
  const isLiked = rating === CHAT_MESSAGE_RATING.POSITIVE;
  const isDisliked = rating === CHAT_MESSAGE_RATING.NEGATIVE;
  const isRated = isLiked || isDisliked;

  const handleLikeClick = useCallback(() => {
    if (isRated) return;
    sendFeedbackMessage({
      message_id: messageId,
      rating: CHAT_MESSAGE_RATING.POSITIVE,
    })
      .then(() => onRatingChange?.(CHAT_MESSAGE_RATING.POSITIVE))
      .catch(err => console.error('Error sending like rating:', err));
  }, [isRated, messageId, onRatingChange, sendFeedbackMessage]);

  const handleDislikeClick = useCallback(() => {
    if (isRated) return;
    sendFeedbackMessage({
      message_id: messageId,
      rating: CHAT_MESSAGE_RATING.NEGATIVE,
    })
      .then(() => onRatingChange?.(CHAT_MESSAGE_RATING.NEGATIVE))
      .catch(err => console.error('Error sending dislike rating:', err));
  }, [isRated, messageId, onRatingChange, sendFeedbackMessage]);

  const submitFeedback = useCallback(
    async ({ feedback, reasons }: { feedback: string; reasons: string[] }) => {
      const sid = sessionId ?? '';
      try {
        await sendFeedback({
          message_id: messageId,
          feedback,
          session_id: sid,
          reasons,
        });
      } catch (err) {
        console.error('Error sending feedback:', err);
      }
    },
    [messageId, sessionId, sendFeedback]
  );

  return {
    isLiked,
    isDisliked,
    isRated,
    handleLikeClick,
    handleDislikeClick,
    submitFeedback,
  };
}
